/**
 * RevenueHero Adapter - Trigger RevenueHero calendar scheduling
 *
 * This adapter triggers the RevenueHero scheduler after form submission.
 * The consumer website loads the RevenueHero SDK separately.
 *
 * @example
 * const adapter = createRevenueHeroAdapter({
 *   routerId: 'your-router-id'
 * });
 * await adapter.submit(formData, context);
 */

import type {
  FormData,
  SubmissionContext,
  SubmissionResult,
  SubmissionAdapter,
  RevenueHeroConfig,
} from './types.js';

/**
 * Check if RevenueHero SDK is loaded
 */
export function isRevenueHeroLoaded(): boolean {
  return typeof window !== 'undefined' && typeof window.RevenueHero !== 'undefined';
}

/**
 * Create a RevenueHero submission adapter
 *
 * Note: This adapter triggers the RevenueHero scheduler but doesn't
 * submit data to a backend. Use it in combination with another adapter
 * (like HubSpot or webhook) for data persistence.
 */
export function createRevenueHeroAdapter(config: RevenueHeroConfig): SubmissionAdapter {
  const { routerId, fieldMapping = {}, mock = false } = config;

  return {
    name: 'revenuehero',

    mapFields(data: FormData): FormData {
      const mapped: FormData = {};
      for (const [key, value] of Object.entries(data)) {
        const mappedKey = fieldMapping[key] || key;
        mapped[mappedKey] = value;
      }
      return mapped;
    },

    async submit(data: FormData, _context: SubmissionContext): Promise<SubmissionResult> {
      // Mock mode for testing
      if (mock) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return { success: true, data: { mock: true, scheduled: true } };
      }

      if (!routerId) {
        return { success: false, error: 'RevenueHero router ID is required' };
      }

      // Check if SDK is loaded
      if (!isRevenueHeroLoaded()) {
        console.warn(
          '[RevenueHeroAdapter] RevenueHero SDK not loaded. ' +
            'Load the SDK in your page: <script src="https://app.revenuehero.io/js/widget.js"></script>'
        );
        return {
          success: false,
          error: 'RevenueHero SDK not loaded. Please load the SDK in your page.',
        };
      }

      const mappedData = this.mapFields(data);

      // Extract email (required for RevenueHero)
      const email = String(mappedData.email || mappedData.workEmail || '');
      if (!email) {
        return { success: false, error: 'Email is required for RevenueHero scheduling' };
      }

      try {
        // Trigger RevenueHero scheduler
        window.RevenueHero!.schedule({
          routerId,
          email,
          ...mappedData,
        });

        return {
          success: true,
          data: { scheduled: true, email },
        };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'RevenueHero error';
        return { success: false, error: errorMessage };
      }
    },
  };
}

export type { RevenueHeroConfig };
